import React from "react";
import {
  Container,
  Title,
  Text,
  createStyles,
  Group,
  Button,
} from "@mantine/core";

import { AxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import NotAuthenticated from "./NotAuthenticated";
import NotFound from "./NotFound";

type ErrorPageProps = {
  errors: AxiosError | null;
};

const useStyles = createStyles((theme) => ({
  root: {
    paddingTop: 80,
    paddingBottom: 80,
  },

  label: {
    textAlign: "center",
    fontWeight: 900,
    fontSize: 220,
    lineHeight: 1,
    marginBottom: theme.spacing.xl * 1.5,
    color:
      theme.colorScheme === "dark"
        ? theme.colors.dark[4]
        : theme.colors.gray[2],

    [theme.fn.smallerThan("sm")]: {
      fontSize: 120,
    },
  },

  title: {
    fontFamily: `Inter, ${theme.fontFamily}`,
    textAlign: "center",
    fontWeight: 900,
    fontSize: 38,

    [theme.fn.smallerThan("sm")]: {
      fontSize: 32,
    },
  },

  description: {
    maxWidth: 500,
    margin: "auto",
    marginTop: theme.spacing.xl,
    marginBottom: theme.spacing.xl * 1.5,
  },
}));

const ErrorPage: React.FC<ErrorPageProps> = ({ errors }) => {
  const { classes } = useStyles();
  const navigate = useNavigate();

  if (errors && errors.response) {
    switch (errors.response.status) {
      case 401:
        return <NotAuthenticated />;
      case 403:
        return (
          <Container className={classes.root}>
            <div className={classes.label}>{errors.response.status}</div>
            <Title className={classes.title}>
              You&apos;re not permitted to see this.
            </Title>

            {
              // eslint-disable-next-line no-prototype-builtins
              errors.response.data.hasOwnProperty("message") ? (
                <Text
                  color="dimmed"
                  size="lg"
                  align="center"
                  className={classes.description}
                >
                  {errors.response.data.message}
                </Text>
              ) : (
                <>
                  <Text
                    color="dimmed"
                    size="lg"
                    align="center"
                    className={classes.description}
                  >
                    This page you&apos;re trying to access has restricted access
                  </Text>
                  <Text
                    color="dimmed"
                    size="lg"
                    align="center"
                    className={classes.description}
                  >
                    If you feel this is a mistake, contact your admin.
                  </Text>
                </>
              )
            }

            <Group position="center">
              <Button
                variant="subtle"
                onClick={() => navigate(-1)}
                size="md"
                color="jem-theme"
              >
                Take me back to home page
              </Button>
            </Group>
          </Container>
        );
      case 404:
        return <NotFound />;
      default:
        return (
          <Container className={classes.root}>
            <div className={classes.label}>{errors.response.status}</div>
            <Title className={classes.title}>
              {" "}
              This page is currently not available.
            </Title>

            {
              // eslint-disable-next-line no-prototype-builtins
              errors.response.data.hasOwnProperty("message") ? (
                <Text
                  color="dimmed"
                  size="lg"
                  align="center"
                  className={classes.description}
                >
                  {errors.response.data.message}
                </Text>
              ) : (
                <Text
                  color="dimmed"
                  size="lg"
                  align="center"
                  className={classes.description}
                >
                  Our servers could not handle your request. Don&apos;t worry,
                  our development team was already notified. Try refreshing the
                  page.
                </Text>
              )
            }
          </Container>
        );
    }
  } else if (errors && errors.request) {
    return (
      <Container className={classes.root}>
        <div className={classes.label}>Error</div>
        <Title className={classes.title}>Oops, something went wrong</Title>

        {
          // eslint-disable-next-line no-prototype-builtins
          errors.request.response ? (
            <Text
              color="dimmed"
              size="lg"
              align="center"
              className={classes.description}
            >
              {errors.request.response}
            </Text>
          ) : (
            <>
              <Text
                color="dimmed"
                size="lg"
                align="center"
                className={classes.description}
              >
                This page is currently not available.
              </Text>
              <Text
                color="dimmed"
                size="lg"
                align="center"
                className={classes.description}
              >
                We cannot handle your request right now, please wait for a
                couple of minutes and refresh the page. Our team is already
                working on this issue.{" "}
              </Text>
            </>
          )
        }
      </Container>
    );
  } else {
    return (
      <Container className={classes.root}>
        <div className={classes.label}>Error</div>
        <Title className={classes.title}>Oops, something went wrong</Title>
        <Text
          color="dimmed"
          size="lg"
          align="center"
          className={classes.description}
        >
          This page is currently not available.
        </Text>
        <Text
          color="dimmed"
          size="lg"
          align="center"
          className={classes.description}
        >
          We cannot handle your request right now, please wait for a couple of
          minutes and refresh the page. Our team is already working on this
          issue.{" "}
        </Text>
      </Container>
    );
  }
};

export default ErrorPage;
